import { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import { stochasticApi, type StateSpec, type StochasticRun } from '../api/stochasticClient';
import { StochasticExplainer } from '../components/StochasticExplainer';

export function StochasticMacroPage() {
  const [data, setData] = useState<StochasticRun | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [specs, setSpecs] = useState<StateSpec[]>([]);

  const load = () => {
    setLoading(true);
    setError(null);
    stochasticApi.run(undefined, 4000)
      .then(setData)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); stochasticApi.stateRegistry().then(setSpecs).catch(() => setSpecs([])); }, []);

  const gdpQ = data?.monte_carlo.quantiles.gdp_growth;
  const path = data?.monte_carlo.gdp_path_quantiles;
  const years = path ? path.p50.map((_, i) => i * 0.25) : [];

  return (
    <div>
      <h1 className="page-title">Stochastic Macro &amp; G-Cubed Hybrid</h1>
      <p className="page-subtitle">
        Government-scale SDE ({data?.meta.n_state_variables ?? 19} state variables), Monte Carlo paths,
        2D Fokker–Planck check, and McKibbin-style hybrid global block. Full G-Cubed can be imported when licensed.
      </p>

      <button type="button" className="btn-primary" onClick={load} disabled={loading} style={{ marginBottom: '1rem' }}>
        {loading ? 'Running 4,000 paths…' : 'Re-run stochastic analysis'}
      </button>
      {error && <div className="error">{error}</div>}
      {!data && !error && <p>Loading…</p>}

      {data && (
        <>
          <div className="card" style={{ marginBottom: '1rem' }}>
            <h3>GDP growth — terminal distribution (5-year horizon)</h3>
            {gdpQ && (
              <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                <div><div style={{ fontSize: '0.75rem', color: '#6b7280' }}>p10</div><strong>{gdpQ.p10.toFixed(2)}%</strong></div>
                <div><div style={{ fontSize: '0.75rem', color: '#6b7280' }}>p50</div><strong>{gdpQ.p50.toFixed(2)}%</strong></div>
                <div><div style={{ fontSize: '0.75rem', color: '#6b7280' }}>p90</div><strong>{gdpQ.p90.toFixed(2)}%</strong></div>
                <div><div style={{ fontSize: '0.75rem', color: '#6b7280' }}>σ</div><strong>{gdpQ.std.toFixed(2)}</strong></div>
              </div>
            )}
          </div>

          {path && years.length > 0 && (
            <div className="card" style={{ marginBottom: '1rem' }}>
              <h3>GDP growth fan chart (years from now)</h3>
              <Plot
                data={[
                  { x: years, y: path.p90, mode: 'lines', line: { width: 0 }, showlegend: false },
                  { x: years, y: path.p10, mode: 'lines', fill: 'tonexty', name: 'p10–p90', line: { color: '#93c5fd' } },
                  { x: years, y: path.p50, mode: 'lines', name: 'Median path', line: { color: '#1e3a5f', width: 2 } },
                ]}
                layout={{
                  autosize: true,
                  height: 320,
                  margin: { l: 48, r: 16, t: 8, b: 40 },
                  xaxis: { title: 'Years' },
                  yaxis: { title: 'GDP growth (%)' },
                  paper_bgcolor: 'transparent',
                  plot_bgcolor: 'transparent',
                }}
                config={{ displayModeBar: false }}
                style={{ width: '100%' }}
              />
            </div>
          )}

          <div className="card" style={{ marginBottom: '1rem' }}>
            <h3>G-Cubed hybrid (5 sectors)</h3>
            <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>{data.gcubed.notes.join(' · ')}</p>
            <p>Sustainable GDP ceiling (hybrid): <strong>{data.gcubed.sustainable_gdp_ceiling.toFixed(2)}%</strong></p>
            <table className="data-table">
              <thead><tr><th>Sector</th><th>Growth (%)</th></tr></thead>
              <tbody>
                {Object.entries(data.gcubed.sector_growth).map(([k, v]) => (
                  <tr key={k}><td>{k}</td><td>{v.toFixed(2)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card" style={{ marginBottom: '1rem' }}>
            <h3>Fokker–Planck vs Monte Carlo (GDP–inflation slice)</h3>
            <p style={{ fontSize: '0.85rem' }}>
              2D FPE median GDP: {String(data.fokker_planck_check.gdp_marginal_p50)} ·
              MC median: {String(data.fokker_planck_check.mc_gdp_p50)} ·
              gap: {String(data.fokker_planck_check.mc_fpe_p50_gap)}
            </p>
          </div>

          <div className="card">
            <h3>Wilcoxon validation (actual vs deterministic forecast)</h3>
            <p>{data.wilcoxon.verdict}</p>
            <p style={{ fontSize: '0.85rem' }}>n = {data.wilcoxon.n}, p ≈ {data.wilcoxon.p_value ?? '—'}</p>
          </div>

          <StochasticExplainer data={data} specs={specs} />
        </>
      )}
    </div>
  );
}
